import {PortalContext, registerPortalRoot} from '@primer/react'
import {type ReactNode, useEffect, useMemo, useRef} from 'react'

export const ARTIFACTS_PANEL_PORTAL = 'artifacts-panel-portal'

type Props = {children: ReactNode}

/**
 * Renders children inside a PortalContext that points Primer popovers
 * (ActionMenu.Overlay, Tooltip overlays, etc.) at a panel-internal
 * container instead of body. Keeps anchors AND their overlays in the
 * same containing block (the position:fixed panel) — fixes both:
 *  1. CSS Anchor Positioning failing for anchors inside position:fixed
 *  2. Focus-induced scroll-to-top from body-level (0,0) portal mount
 */
export function ArtifactsPanelPortalProvider({children}: Props) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    registerPortalRoot(el, ARTIFACTS_PANEL_PORTAL)
    return () => {
      registerPortalRoot(document.body, ARTIFACTS_PANEL_PORTAL)
    }
  }, [])
  const portalContextValue = useMemo(() => ({portalContainerName: ARTIFACTS_PANEL_PORTAL}), [])
  return (
    <PortalContext value={portalContextValue}>
      {children}
      <div ref={ref} data-artifacts-portal-root="" />
    </PortalContext>
  )
}
